import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardCheck, Play, Search, CheckCircle2, Loader2,
  ChevronRight, Clock, Camera
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { API } from "../../App";

const STAGES = [
  { id: "pending", label: "Pending", icon: Clock, color: "slate" },
  { id: "checked_in", label: "Checked In", icon: ClipboardCheck, color: "blue" },
  { id: "in_progress", label: "In Progress", icon: Play, color: "yellow" },
  { id: "qc", label: "Quality Check", icon: Search, color: "purple" },
  { id: "completed", label: "Completed", icon: CheckCircle2, color: "green" }
];

const JobProgressTracker = ({ 
  bookingId, 
  currentStage = "pending",
  onStageChange,
  onCheckInClick,
  onCompleteClick,
  compact = false
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [stageInfo, setStageInfo] = useState(null);

  useEffect(() => {
    if (bookingId) {
      loadStageInfo();
    }
  }, [bookingId]);

  const loadStageInfo = async () => {
    try {
      const response = await axios.get(`${API}/jobs/stage/${bookingId}`);
      setStageInfo(response.data);
    } catch (error) {
      console.error("Failed to load stage info:", error);
    }
  };

  const currentStageIndex = STAGES.findIndex(s => s.id === (stageInfo?.stage || currentStage));

  const handleStageChange = async (newStage) => {
    // Special handling for check-in
    if (newStage === "checked_in" && (!stageInfo || !stageInfo.checked_in)) {
      onCheckInClick?.();
      return;
    }

    // Special handling for completion - if onCompleteClick is provided, use it
    // Otherwise, just update the stage directly
    if (newStage === "completed") {
      if (onCompleteClick) {
        onCompleteClick?.();
        return;
      }
      // Fall through to update stage directly if no handler provided
    }

    setIsUpdating(true);
    try {
      const response = await axios.post(`${API}/jobs/stage/${bookingId}?new_stage=${newStage}`);
      setStageInfo(prev => ({
        ...prev,
        stage: newStage,
        stage_history: response.data.stage_history
      }));
      onStageChange?.(newStage);
      toast.success(`Job moved to ${STAGES.find(s => s.id === newStage)?.label}`);
    } catch (error) {
      console.error("Failed to update stage:", error);
      toast.error(error.response?.data?.detail || "Failed to update stage");
    } finally {
      setIsUpdating(false);
    }
  };

  const getNextStage = () => {
    const idx = currentStageIndex;
    if (idx < 0 || idx >= STAGES.length - 1) return null;
    return STAGES[idx + 1];
  };

  const nextStage = getNextStage();
  const actualStage = stageInfo?.stage || currentStage;

  if (compact) {
    // Compact horizontal progress bar
    return (
      <div className="flex items-center gap-1">
        {STAGES.slice(1).map((stage, idx) => {
          const stageIdx = idx + 1; // Offset because we skip "pending"
          const isActive = STAGES.findIndex(s => s.id === actualStage) >= stageIdx;
          const isCurrent = actualStage === stage.id;
          
          return (
            <React.Fragment key={stage.id}>
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                  isActive
                    ? `bg-${stage.color}-500/20 text-${stage.color}-400 border border-${stage.color}-500/50`
                    : "bg-slate-800 text-slate-500 border border-slate-700"
                } ${isCurrent ? "ring-2 ring-offset-1 ring-offset-slate-900 ring-saturn-orange" : ""}`}
                title={stage.label}
              >
                <stage.icon className="w-3 h-3" />
              </div>
              {idx < STAGES.slice(1).length - 1 && (
                <div className={`w-4 h-0.5 ${isActive ? "bg-saturn-orange" : "bg-slate-700"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-lg p-4 space-y-4">
      {/* Stage Progress Bar */}
      <div className="flex items-center justify-between">
        {STAGES.map((stage, idx) => {
          const isActive = currentStageIndex >= idx;
          const isCurrent = actualStage === stage.id;
          const StageIcon = stage.icon;
          
          return (
            <React.Fragment key={stage.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isActive
                      ? isCurrent
                        ? "bg-saturn-orange text-white shadow-lg shadow-saturn-orange/30"
                        : "bg-green-500/20 text-green-400 border border-green-500/50"
                      : "bg-slate-800 text-slate-500 border border-slate-700"
                  }`}
                >
                  <StageIcon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-1 ${isCurrent ? "text-saturn-orange font-medium" : isActive ? "text-green-400" : "text-slate-500"}`}>
                  {stage.label}
                </span>
              </div>
              
              {idx < STAGES.length - 1 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStageIndex > idx ? "bg-green-500" : "bg-slate-700"
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Stage Info */}
      {stageInfo && (
        <div className="grid grid-cols-3 gap-2 text-xs text-slate-400">
          {stageInfo.checked_in_at && (
            <div>
              <span className="block text-slate-500">Checked In</span>
              <span className="text-white">
                {new Date(stageInfo.checked_in_at).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          {stageInfo.started_at && (
            <div>
              <span className="block text-slate-500">Started</span>
              <span className="text-white">
                {new Date(stageInfo.started_at).toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
          {stageInfo.before_photos_count > 0 && (
            <div className="flex items-center gap-1">
              <Camera className="w-3 h-3" />
              <span>{stageInfo.before_photos_count} before photos</span>
            </div>
          )}
        </div>
      )}

      {/* Action Button */}
      {nextStage && actualStage !== "completed" && (
        <div className="flex justify-end">
          <Button
            onClick={() => handleStageChange(nextStage.id)}
            disabled={isUpdating}
            className={`${
              nextStage.id === "checked_in" 
                ? "bg-blue-600 hover:bg-blue-700" 
                : nextStage.id === "completed"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-saturn-orange hover:bg-saturn-orange/80"
            }`}
            data-testid={`stage-btn-${nextStage.id}`}
          >
            {isUpdating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <nextStage.icon className="w-4 h-4 mr-2" />
            )}
            {nextStage.id === "checked_in" 
              ? "Check In Vehicle" 
              : nextStage.id === "in_progress"
              ? "Start Work"
              : nextStage.id === "qc"
              ? "Send to QC"
              : "Complete Job"
            }
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Completed Badge */}
      {actualStage === "completed" && (
        <div className="flex justify-center">
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm px-4 py-1">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Job Completed
          </Badge>
        </div>
      )}
    </div>
  );
};

export default JobProgressTracker;
